import { useCallback, useMemo, useState } from 'react'
import { LayoutGrid, Plus, Minus, Star, StarOff } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { useToast } from '@/providers/UiProviders'
import {
  fetchCatalogApps,
  setAppCatalog,
  setAppFeatured,
  APP_MOD_STR as S,
  type CatalogAppRow,
} from './appModApi'

/**
 * A5.21 — Katalogga qo'shish/olib tashlash. GET /admin/mini-apps/catalog (lane-A
 * greenfield → fail-soft bo'sh). Katalog ko'rinishi + tavsiya (featured) toggle →
 * POST /admin/mini-apps/:id/catalog · /feature.
 */
export function AppCatalogScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(S.catalogUpdated, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const columns = useMemo<AdminColumn<CatalogAppRow>[]>(
    () => [
      {
        key: 'title',
        header: S.app,
        type: 'text',
        minWidth: 200,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title}</span>
            <code className="text-xs text-accent">/{r.short_name}</code>
          </div>
        ),
      },
      { key: 'category', header: S.category, type: 'text', width: 150, render: (r) => r.category || '—' },
      {
        key: 'in_catalog',
        header: S.inCatalog,
        type: 'text',
        width: 130,
        render: (r) =>
          r.in_catalog ? <StatusBadge status="active" label={S.inCatalog} /> : <span className="text-tertiary">—</span>,
      },
      {
        key: 'featured',
        header: S.featured,
        type: 'text',
        width: 130,
        render: (r) =>
          r.featured ? (
            <span className="inline-flex items-center gap-1 text-accent">
              <Star size={14} /> {S.featured}
            </span>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      { key: 'installs', header: S.installs, type: 'number', width: 110, align: 'right' },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<CatalogAppRow>[]>(
    () => [
      {
        id: 'add',
        label: S.addToCatalog,
        icon: <Plus size={13} />,
        visible: (r) => !r.in_catalog,
        onSelect: (r) => void run(() => setAppCatalog(r.id, true)),
      },
      {
        id: 'remove',
        label: S.removeFromCatalog,
        icon: <Minus size={13} />,
        danger: true,
        visible: (r) => r.in_catalog,
        onSelect: (r) => void run(() => setAppCatalog(r.id, false)),
      },
      {
        id: 'feature',
        label: S.makeFeatured,
        icon: <Star size={13} />,
        visible: (r) => r.in_catalog && !r.featured,
        onSelect: (r) => void run(() => setAppFeatured(r.id, true)),
      },
      {
        id: 'unfeature',
        label: S.unfeature,
        icon: <StarOff size={13} />,
        visible: (r) => r.featured,
        onSelect: (r) => void run(() => setAppFeatured(r.id, false)),
      },
    ],
    [run],
  )

  const fetcher = useCallback(fetchCatalogApps, [])

  return (
    <>
      <PageHeader title={S.catalogTitle} screenId="A5.21" />
      <DataTable<CatalogAppRow>
        key={reloadKey}
        screenId="A5.21"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <LayoutGrid size={28} strokeWidth={1.5} />, title: S.catalogEmpty }}
      />
    </>
  )
}
